import React, { useState, useRef, useEffect } from 'react';
import { 
    Badge, 
    IconButton, 
    Popover, 
    List, 
    ListItem, 
    ListItemText, 
    ListItemIcon, 
    Typography, 
    Box, 
    Button,
    CircularProgress,
    Chip,
    useMediaQuery,
    useTheme
} from '@mui/material';
import {
    Notifications as NotificationsIcon,
    NotificationsNone as NotificationsNoneIcon,
    Work as WorkIcon,
    Assignment as AssignmentIcon,
    Schedule as ScheduleIcon,
    CheckCircle as CheckCircleIcon,
    Description as DescriptionIcon,
    Business as BusinessIcon,
    Info as InfoIcon,
    Close as CloseIcon,
    AutoAwesome as AutoAwesomeIcon,
    HourglassTop as HourglassTopIcon,
    Mic as MicIcon
} from '@mui/icons-material';
import { motion, AnimatePresence } from 'framer-motion';
import { useNotifications } from '../contexts/NotificationContext';
import { useNavigate } from 'react-router-dom';

const getNotificationMeta = (notification) => {
    const type = notification?.notification_type || '';
    const title = (notification?.title || '').toLowerCase();
    const hasAuditId = Boolean(notification?.data?.audit_id);
    const isJdTitle = title.includes('jd ready') || title.includes('job description') || title.includes('jd draft');
    const isJdType = type.includes('jd_generation') || type.includes('generated_jd') || type.includes('job_description');
    const isMatchingType = type === 'matching_complete' || title.includes('matching complete');
    const isJdNotification = isJdType || (hasAuditId && isJdTitle);

    if (isMatchingType) {
        return {
            icon: <AutoAwesomeIcon sx={{ color: '#0891b2' }} />,
            color: '#0891b2',
            accentBg: '#ecfeff',
            isMatchingType: true,
        };
    }

    if (isJdNotification) {
        const isReady = title.includes('ready') || title.includes('review');
        return {
            icon: isReady ? <AutoAwesomeIcon sx={{ color: '#16a34a' }} /> : <HourglassTopIcon sx={{ color: '#7c3aed' }} />,
            color: isReady ? '#16a34a' : '#7c3aed',
            accentBg: isReady ? '#dcfce7' : '#f3e8ff',
            isMatchingType: false,
        };
    }

    // Default icon/color mapping
    const getDefaultMeta = (t) => {
        switch (t) {
            case 'job_application':
            case 'new_candidate':
                return {
                    icon: <WorkIcon sx={{ color: '#1976d2' }} />,
                    color: '#1976d2',
                    accentBg: '#dbeafe',
                };
            case 'application_status':
            case 'status_change':
                return {
                    icon: <AssignmentIcon sx={{ color: '#0288d1' }} />,
                    color: '#0288d1',
                    accentBg: '#e0f2fe',
                };
            case 'interview_scheduled':
                return {
                    icon: <ScheduleIcon sx={{ color: '#ed6c02' }} />,
                    color: '#ed6c02',
                    accentBg: '#ffedd5',
                };
            case 'interview_completed':
                return {
                    icon: <CheckCircleIcon sx={{ color: '#2e7d32' }} />,
                    color: '#2e7d32',
                    accentBg: '#dcfce7',
                };
            case 'resume_uploaded':
                return {
                    icon: <DescriptionIcon sx={{ color: '#7b1fa2' }} />,
                    color: '#7b1fa2',
                    accentBg: '#f3e8ff',
                };
            case 'job_posted':
            case 'company_assigned':
                return {
                    icon: <BusinessIcon sx={{ color: '#1976d2' }} />,
                    color: '#1976d2',
                    accentBg: '#dbeafe',
                };
            case 'recording_uploaded':
            case 'audio_uploaded':
                return {
                    icon: <MicIcon sx={{ color: '#7c3aed' }} />,
                    color: '#7c3aed',
                    accentBg: '#f3e8ff',
                };
            default:
                return {
                    icon: <InfoIcon sx={{ color: '#64748b' }} />,
                    color: '#64748b',
                    accentBg: '#e2e8f0',
                };
        }
    };

    return {
        ...getDefaultMeta(type),
        isMatchingType: false
    };
};

const getNotificationIcon = (type) => {
    switch (type) {
        case 'new_candidate':
        case 'job_application':
            return <WorkIcon color="primary" />;
        case 'interview_scheduled':
            return <ScheduleIcon color="success" />;
        case 'interview_completed':
            return <CheckCircleIcon color="success" />;
        case 'interview_cancelled':
            return <CloseIcon color="error" />;
        case 'feedback_submitted':
            return <AssignmentIcon color="secondary" />;
        case 'status_change':
        case 'application_status':
            return <AssignmentIcon color="info" />;
        case 'jd_assigned':
            return <AssignmentIcon sx={{ color: '#ed6c02' }} />;
        case 'matching_complete':
            return <AutoAwesomeIcon sx={{ color: '#0891b2' }} />;
        case 'company_assigned':
            return <BusinessIcon sx={{ color: '#1976d2' }} />;
        case 'generated_jd':
            return <DescriptionIcon color="primary" />;
        case 'jd_generation':
            return <HourglassTopIcon sx={{ color: '#7c3aed' }} />;
        default:
            return <NotificationsIcon />;
    }
};

const getNotificationColor = (type) => {
    switch (type) {
        case 'new_candidate':
        case 'job_application':
            return '#1976d2';
        case 'interview_scheduled':
        case 'interview_completed':
            return '#2e7d32';
        case 'interview_cancelled':
            return '#d32f2f';
        case 'feedback_submitted':
            return '#9c27b0';
        case 'status_change':
        case 'application_status':
            return '#0288d1';
        case 'jd_assigned':
            return '#ed6c02';
        case 'matching_complete':
            return '#0891b2';
        case 'company_assigned':
            return '#1976d2';
        case 'generated_jd':
            return '#1976d2';
        case 'jd_generation':
            return '#7c3aed';
        default:
            return '#757575';
    }
};

const DETAIL_PANEL_WIDTH = 340;
const DETAIL_PANEL_TOP = 16;

const OPEN_EASE = [0.65, 0, 0.22, 1];
const OPEN_DURATION = 0.82;
const CLOSE_DURATION = 0.48;

const GeniePanel = ({ children, origin = 'right', width, style }) => {
    const fromRight = origin === 'right';
    const originPoint = fromRight ? '100% 50%' : '50% 0%';
    const collapsed = {
        clipPath: `circle(1.2% at ${originPoint})`,
        opacity: 0.35,
        scale: 0.98,
    };
    const expanded = {
        clipPath: `circle(150% at ${originPoint})`,
        opacity: 1,
        scale: 1,
    };

    return (
        <motion.div
            initial={collapsed}
            animate={expanded}
            exit={{
                ...collapsed,
                transition: { duration: CLOSE_DURATION, ease: OPEN_EASE },
            }}
            transition={{
                duration: OPEN_DURATION,
                ease: OPEN_EASE,
                opacity: { duration: 0.62, ease: OPEN_EASE },
                scale: { duration: 0.78, ease: OPEN_EASE },
            }}
            style={{
                ...style,
                width: width || style?.width,
                overflow: 'hidden',
                backgroundColor: '#fff',
                borderRadius: 16,
                transformOrigin: fromRight ? 'right center' : 'top center',
                willChange: 'clip-path, opacity, transform',
            }}
        >
            {children}
        </motion.div>
    );
};

const NotificationDetailCard = ({ notification, onClose, onNavigate, isMobile }) => {
    if (!notification) return null;
    const meta = getNotificationMeta(notification);

    return (
        <Box
            onClick={(event) => event.stopPropagation()}
            sx={{
                width: isMobile ? '100%' : DETAIL_PANEL_WIDTH,
                p: { xs: 1.5, sm: 2 },
                borderRadius: 2,
                backgroundColor: '#fff',
                boxShadow: '0 18px 48px rgba(15, 23, 42, 0.18), 0 0 0 1px rgba(15, 23, 42, 0.06)',
                border: '1px solid rgba(0, 0, 0, 0.08)',
            }}
        >
            <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: { xs: 1.5, sm: 2 }, gap: 1 }}>
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, flex: 1, minWidth: 0 }}>
                    {getNotificationIcon(notification.notification_type)}
                    <Typography
                        variant="subtitle1"
                        sx={{
                            fontFamily: 'Poppins, sans-serif',
                            fontWeight: 600,
                            color: '#262626',
                            fontSize: { xs: '0.9rem', sm: '1rem' },
                            overflow: 'hidden',
                            textOverflow: 'ellipsis',
                        }}
                    >
                        {notification.title}
                    </Typography>
                </Box>
                <IconButton size="small" onClick={onClose} sx={{ flexShrink: 0 }}>
                    <CloseIcon fontSize="small" />
                </IconButton>
            </Box>

            <Typography
                variant="body2"
                sx={{
                    fontFamily: 'Poppins, sans-serif',
                    color: 'text.secondary',
                    mb: { xs: 1.5, sm: 2 },
                    lineHeight: 1.6,
                    fontSize: { xs: '0.8rem', sm: '0.875rem' },
                }}
            >
                {notification.message}
            </Typography>

            {notification.data && Object.keys(notification.data).length > 0 && (
                <Box sx={{ mb: { xs: 1.5, sm: 2 } }}>
                    <Typography
                        variant="caption"
                        sx={{
                            fontFamily: 'Poppins, sans-serif',
                            fontWeight: 600,
                            color: 'text.secondary',
                            textTransform: 'uppercase',
                            letterSpacing: 0.5,
                            fontSize: { xs: '0.65rem', sm: '0.75rem' },
                        }}
                    >
                        Details
                    </Typography>
                    <Box sx={{ mt: 1, display: 'flex', flexWrap: 'wrap', gap: 0.5 }}>
                        {Object.entries(notification.data)
                            .filter(([key]) => !['event', 'audit_id', 'type', 'id'].includes(key))
                            .map(([key, value]) => {
                                let label = key.replace(/_/g, ' ').replace(/\b\w/g, (l) => l.toUpperCase());
                                let displayValue = value;

                                if (key === 'job_description_id' && meta.isMatchingType) {
                                    const match = notification.message.match(/'([^']+)'/);
                                    if (match) {
                                        label = 'Job Description';
                                        displayValue = match[1];
                                    } else {
                                        label = 'Job ID';
                                    }
                                }

                                return (
                                    <Chip
                                        key={key}
                                        label={`${label}: ${displayValue}`}
                                        size="small"
                                        sx={{
                                            fontFamily: 'Poppins, sans-serif',
                                            fontSize: { xs: '0.65rem', sm: '0.7rem' },
                                            height: { xs: '24px', sm: '28px' },
                                            backgroundColor: 'rgba(0, 0, 0, 0.04)',
                                            borderColor: 'rgba(0, 0, 0, 0.08)',
                                            fontWeight: label === 'Job Description' ? 600 : 400,
                                            color: label === 'Job Description' ? '#0891b2' : 'inherit',
                                        }}
                                    />
                                );
                            })}
                    </Box>
                    {meta.isMatchingType && (
                        <Button
                            variant="contained"
                            size="small"
                            fullWidth
                            sx={{
                                mt: 2,
                                fontFamily: 'Poppins, sans-serif',
                                fontWeight: 600,
                                borderRadius: 2,
                                background: 'linear-gradient(135deg, #0ea5e9 0%, #d946ef 100%)',
                            }}
                            onClick={() => {
                                const jdId = notification.data?.job_description_id;
                                onNavigate(`/bulk-schedule${jdId ? `?jd_id=${jdId}` : ''}`);
                            }}
                        >
                            Go to Bulk Scheduling
                        </Button>
                    )}
                    {notification.data?.audit_id && (
                        <Button
                            variant="contained"
                            color="primary"
                            size="small"
                            fullWidth
                            sx={{ mt: 2, fontFamily: 'Poppins, sans-serif', fontWeight: 600, borderRadius: 2 }}
                            onClick={() => {
                                onNavigate(`/jobdescriptions?review_audit_id=${notification.data.audit_id}`);
                            }}
                        >
                            Review Generated JD
                        </Button>
                    )}
                </Box>
            )}

            <Typography
                variant="caption"
                sx={{
                    fontFamily: 'Poppins, sans-serif',
                    color: 'text.secondary',
                    fontSize: { xs: '0.65rem', sm: '0.75rem' },
                }}
            >
                {notification.time_ago}
            </Typography>
        </Box>
    );
};

export default function NotificationBell() {
    const theme = useTheme();
    const isMobile = useMediaQuery(theme.breakpoints.down('sm'));
    const navigate = useNavigate();
    const { 
        notifications, 
        unreadCount, 
        loading, 
        markAsRead, 
        markSingleAsRead,
        fetchNotifications 
    } = useNotifications();
    
    const [anchorEl, setAnchorEl] = useState(null);
    const [selectedNotification, setSelectedNotification] = useState(null);
    const [dockLeft, setDockLeft] = useState(true);
    const popoverRef = useRef(null);

    const handleClick = (event) => {
        setAnchorEl(event.currentTarget);
        fetchNotifications();
    };

    const handleClose = () => {
        setAnchorEl(null);
        setSelectedNotification(null);
    };

    const updateDockSide = () => {
        const paper = popoverRef.current;
        if (!paper) return;
        setDockLeft(paper.getBoundingClientRect().left >= DETAIL_PANEL_WIDTH + 24);
    };

    const handleNotificationClick = async (notification) => {
        if (!notification.is_read) {
            try {
                await markSingleAsRead(notification.id);
            } catch (err) {
                console.error('Error marking notification as read:', err);
            }
        }
        if (selectedNotification?.id === notification.id) {
            setSelectedNotification(null);
            return;
        }
        updateDockSide();
        setSelectedNotification(notification);
    };

    const handleMarkAllRead = async () => {
        try {
            await markAsRead(null, true);
        } catch (err) {
            console.error('Error marking all notifications as read:', err);
        }
    };

    const handleCloseDetail = () => {
        setSelectedNotification(null);
    };

    const open = Boolean(anchorEl);
    const id = open ? 'notification-popover' : undefined;

    // Close popover when clicking outside
    useEffect(() => {
        const handleClickOutside = (event) => {
            if (popoverRef.current && !popoverRef.current.contains(event.target)) {
                handleClose();
            }
        };

        if (open) {
            document.addEventListener('mousedown', handleClickOutside);
        }

        return () => {
            document.removeEventListener('mousedown', handleClickOutside);
        };
    }, [open]);

    return (
        <>
            <IconButton
                onClick={handleClick}
                sx={{
                    color: '#262626',
                    padding: { xs: '8px', sm: '12px' },
                    '&:hover': {
                        backgroundColor: 'rgba(0, 0, 0, 0.04)',
                    },
                }}
            >
                <Badge 
                    badgeContent={unreadCount} 
                    color="error"
                    max={99}
                    sx={{
                        '& .MuiBadge-badge': {
                            backgroundColor: '#f44336',
                            color: 'white',
                            fontSize: { xs: '0.65rem', sm: '0.75rem' },
                            fontWeight: 600,
                            minWidth: { xs: '16px', sm: '18px' },
                            height: { xs: '16px', sm: '18px' },
                        }
                    }}
                >
                    {unreadCount > 0 ? (
                        <NotificationsIcon sx={{ fontSize: { xs: 20, sm: 24 } }} />
                    ) : (
                        <NotificationsNoneIcon sx={{ fontSize: { xs: 20, sm: 24 } }} />
                    )}
                </Badge>
            </IconButton>

            <Popover
                id={id}
                open={open}
                anchorEl={anchorEl}
                onClose={handleClose}
                anchorOrigin={{
                    vertical: 'bottom',
                    horizontal: isMobile ? 'center' : 'right',
                }}
                transformOrigin={{
                    vertical: 'top',
                    horizontal: isMobile ? 'center' : 'right',
                }}
                PaperProps={{
                    ref: popoverRef,
                    sx: {
                        width: isMobile ? 'calc(100vw - 32px)' : 400,
                        maxWidth: isMobile ? 'calc(100vw - 32px)' : 400,
                        borderRadius: { xs: 1, sm: 2 },
                        boxShadow: '0 8px 32px rgba(0, 0, 0, 0.12)',
                        border: '1px solid rgba(0, 0, 0, 0.08)',
                        margin: { xs: '16px', sm: 0 },
                        overflow: 'visible',
                    }
                }}
            >
                <Box sx={{ position: 'relative' }}>
                    <AnimatePresence>
                        {selectedNotification && !isMobile && dockLeft && (
                            <GeniePanel
                                key="notification-detail-panel"
                                origin="right"
                                width={DETAIL_PANEL_WIDTH}
                                style={{
                                    position: 'absolute',
                                    right: '100%',
                                    top: DETAIL_PANEL_TOP,
                                    marginRight: 12,
                                    zIndex: 2,
                                }}
                            >
                                <NotificationDetailCard
                                    notification={selectedNotification}
                                    onClose={handleCloseDetail}
                                    onNavigate={(path) => {
                                        handleClose();
                                        navigate(path);
                                    }}
                                    isMobile={false}
                                />
                            </GeniePanel>
                        )}
                    </AnimatePresence>
                <Box sx={{ p: { xs: 1.5, sm: 2 }, borderBottom: '1px solid rgba(0, 0, 0, 0.08)' }}>
                    <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', gap: 1 }}>
                        <Typography 
                            variant={isMobile ? "subtitle1" : "h6"}
                            sx={{ 
                                fontFamily: 'Poppins, sans-serif',
                                fontWeight: 600,
                                color: '#262626',
                                fontSize: { xs: '0.95rem', sm: '1.25rem' }
                            }}
                        >
                            Notifications
                        </Typography>
                        {unreadCount > 0 && (
                            <Button
                                size="small"
                                onClick={handleMarkAllRead}
                                sx={{
                                    fontFamily: 'Poppins, sans-serif',
                                    fontWeight: 500,
                                    textTransform: 'none',
                                    fontSize: { xs: '0.7rem', sm: '0.75rem' },
                                    padding: { xs: '4px 8px', sm: '6px 16px' }
                                }}
                            >
                                Mark all read
                            </Button>
                        )}
                    </Box>
                </Box>

                {loading ? (
                    <Box sx={{ display: 'flex', justifyContent: 'center', p: { xs: 2, sm: 3 } }}>
                        <CircularProgress size={isMobile ? 20 : 24} />
                    </Box>
                ) : notifications.length === 0 ? (
                    <Box sx={{ p: { xs: 2, sm: 3 }, textAlign: 'center' }}>
                        <NotificationsNoneIcon sx={{ fontSize: { xs: 36, sm: 48 }, color: 'text.secondary', mb: 1 }} />
                        <Typography 
                            variant="body2" 
                            color="text.secondary"
                            sx={{ 
                                fontFamily: 'Poppins, sans-serif',
                                fontSize: { xs: '0.8rem', sm: '0.875rem' }
                            }}
                        >
                            No notifications yet
                        </Typography>
                    </Box>
                ) : (
                    <List sx={{ p: 0, maxHeight: { xs: 'calc(100vh - 200px)', sm: 400 }, overflow: 'auto' }}>
                        {notifications.map((notification) => {
                            const meta = getNotificationMeta(notification);
                            return (
                                <ListItem
                                    key={notification.id}
                                    onClick={() => handleNotificationClick(notification)}
                                    sx={{
                                        cursor: 'pointer',
                                        backgroundColor: selectedNotification?.id === notification.id
                                            ? 'rgba(8, 145, 178, 0.08)'
                                            : notification.is_read ? 'transparent' : 'rgba(25, 118, 210, 0.04)',
                                        borderLeft: selectedNotification?.id === notification.id
                                            ? `3px solid ${meta.color}`
                                            : notification.is_read ? 'none' : `3px solid ${meta.color}`,
                                        '&:hover': {
                                            backgroundColor: 'rgba(0, 0, 0, 0.04)',
                                        },
                                        py: { xs: 1, sm: 1.5 },
                                        px: { xs: 1.5, sm: 2 },
                                    }}
                                >
                                    <ListItemIcon sx={{ minWidth: { xs: 36, sm: 40 } }}>
                                        {meta.icon}
                                    </ListItemIcon>
                                    <ListItemText
                                        primary={
                                            <Typography
                                                variant="body2"
                                                sx={{
                                                    fontFamily: 'Poppins, sans-serif',
                                                    fontWeight: notification.is_read ? 400 : 600,
                                                    color: notification.is_read ? 'text.secondary' : '#262626',
                                                    lineHeight: 1.4,
                                                    fontSize: { xs: '0.875rem', sm: '0.875rem' },
                                                }}
                                            >
                                                {notification.title}
                                            </Typography>
                                        }
                                        secondary={
                                            <Box>
                                                <Typography
                                                    variant="caption"
                                                    sx={{
                                                        fontFamily: 'Poppins, sans-serif',
                                                        color: '#475569',
                                                        display: 'block',
                                                        fontSize: { xs: '0.74rem', sm: '0.78rem' },
                                                        lineHeight: 1.55,
                                                        mt: 0.5,
                                                    }}
                                                >
                                                    {notification.message}
                                                </Typography>
                                                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mt: 0.8 }}>
                                                    <Typography
                                                        variant="caption"
                                                        sx={{
                                                            fontFamily: 'Poppins, sans-serif',
                                                            color: '#64748b',
                                                            fontSize: { xs: '0.68rem', sm: '0.72rem' },
                                                            fontWeight: 500,
                                                        }}
                                                    >
                                                        {notification.time_ago}
                                                    </Typography>
                                                    {meta.isMatchingType && (
                                                        <Chip
                                                            label="Ready to Schedule"
                                                            size="small"
                                                            variant="outlined"
                                                            sx={{
                                                                height: '18px',
                                                                fontSize: '0.6rem',
                                                                borderColor: '#0891b2',
                                                                color: '#0891b2',
                                                                fontFamily: 'Poppins, sans-serif',
                                                                fontWeight: 600,
                                                                px: 0.5,
                                                            }}
                                                        />
                                                    )}
                                                </Box>
                                            </Box>
                                        }
                                    />
                                    {!notification.is_read && (
                                        <Box
                                            sx={{
                                                width: { xs: 6, sm: 8 },
                                                height: { xs: 6, sm: 8 },
                                                borderRadius: '50%',
                                                backgroundColor: meta.color,
                                                ml: { xs: 0.5, sm: 1 },
                                            }}
                                        />
                                    )}
                                </ListItem>
                            );
                        })}
                    </List>
                )}

                    <AnimatePresence>
                        {selectedNotification && (isMobile || !dockLeft) && (
                            <GeniePanel
                                key="notification-detail-panel-overlay"
                                origin="top"
                                width="100%"
                                style={{
                                    position: 'absolute',
                                    left: 8,
                                    right: 8,
                                    top: 56,
                                    zIndex: 3,
                                }}
                            >
                                <NotificationDetailCard
                                    notification={selectedNotification}
                                    onClose={handleCloseDetail}
                                    onNavigate={(path) => {
                                        handleClose();
                                        navigate(path);
                                    }}
                                    isMobile
                                />
                            </GeniePanel>
                        )}
                    </AnimatePresence>
                </Box>
            </Popover>
        </>
    );
}
